import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from './components/layout';
import { LoadingSpinner } from './components/common';
import { DashboardPage, OrganizationsPage, TemplatesPage, InstancesPage } from './pages';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import * as api from './services/api';
import type {
    PageView,
    Organization,
    SurveyTemplate,
    Survey,
    CreateOrganizationPayload,
    UpdateOrganizationPayload,
    UpdateTemplatePayload,
    CreateSurveyPayload,
    UpdateSurveyPayload,
    ToastMessage,
} from './types';

const pageTitles: Record<PageView, string> = {
    dashboard: 'סקירה כללית',
    organizations: 'ניהול ארגונים',
    templates: 'תבניות סקר',
    instances: 'קבוצות סקרים',
    analytics: 'ניתוח תוצאות',
};

const Toast: React.FC<{ toast: ToastMessage; onDismiss: () => void }> = ({ toast, onDismiss }) => {
    useEffect(() => {
        const timer = setTimeout(onDismiss, 4000);
        return () => clearTimeout(timer);
    }, [onDismiss]);

    const iconColors: Record<string, string> = {
        success: 'var(--success-500)',
        error: 'var(--error-500)',
        warning: 'var(--warning-500)',
        info: 'var(--info-500)',
    };

    return (
        <div className={`toast ${toast.type}`}>
            <div className="toast-icon" style={{ color: iconColors[toast.type] }}>
                {toast.type === 'success' && '✓'}
                {toast.type === 'error' && '✕'}
                {toast.type === 'warning' && '⚠'}
                {toast.type === 'info' && 'ℹ'}
            </div>
            <div className="toast-content">
                <div className="toast-title">{toast.title}</div>
                {toast.message && <div className="toast-message">{toast.message}</div>}
            </div>
            <button
                onClick={onDismiss}
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 'var(--space-1)',
                    color: 'var(--neutral-400)',
                }}
            >
                ✕
            </button>
        </div>
    );
};

import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import { LoginPage } from './pages';

const GOOGLE_CLIENT_ID = '704342663288-gjhcr0brevnt4rb5rsata0rrcequ0sn4.apps.googleusercontent.com';

const AppContent: React.FC = () => {
    const { isAuthenticated, logout } = useAuth();
    const [currentPage, setCurrentPage] = useState<PageView>('dashboard');
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [templates, setTemplates] = useState<SurveyTemplate[]>([]);
    const [instances, setInstances] = useState<Survey[]>([]);
    const [loadingOrgs, setLoadingOrgs] = useState(true);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [loadingInstances, setLoadingInstances] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [toasts, setToasts] = useState<ToastMessage[]>([]);
    const [analyticsContext, setAnalyticsContext] = useState<{
        instanceId?: string;
        templateId?: number;
        organizationId?: string;
        respondentId?: string;
    }>({});

    const addToast = useCallback((type: ToastMessage['type'], title: string, message?: string) => {
        const id = Date.now().toString();
        setToasts((prev) => [...prev, { id, type, title, message }]);
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const handleNavigation = useCallback((page: PageView, context: typeof analyticsContext = {}, isPopState = false) => {
        setCurrentPage(page);
        setAnalyticsContext(context);

        if (!isPopState) {
            let path = `/${page}`;
            if (context.organizationId) path += `/org/${context.organizationId}`;
            if (context.instanceId) path += `/instance/${context.instanceId}`;
            if (context.respondentId) path += `/respondent/${context.respondentId}`;
            if (context.templateId) path += `/template/${context.templateId}`;

            window.history.pushState({ page, ...context }, '', path);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;

        const parseUrlAndNavigate = (isInitial = false) => {
            const path = window.location.pathname;
            const segments = path.split('/').filter(Boolean);
            const params = new URLSearchParams(window.location.search);

            const pageFromPath = segments[0] as PageView;
            const page = pageFromPath || (params.get('page') as PageView) || 'dashboard';

            let context: typeof analyticsContext = {};

            for (let i = 1; i < segments.length; i += 2) {
                const key = segments[i];
                const value = segments[i + 1];
                if (!value) break;

                if (key === 'org') context.organizationId = value;
                if (key === 'instance') context.instanceId = value;
                if (key === 'respondent') context.respondentId = value;
                if (key === 'template') context.templateId = parseInt(value, 10);
            }

            if (!context.organizationId) context.organizationId = params.get('organization_id') || undefined;
            if (!context.instanceId) context.instanceId = params.get('instance_id') || undefined;
            if (!context.respondentId) context.respondentId = params.get('respondent_id') || undefined;
            if (!context.templateId && params.get('template_id')) context.templateId = parseInt(params.get('template_id')!, 10);

            const hasContext = context.instanceId || context.templateId || context.organizationId || context.respondentId;

            if (isInitial && hasContext && page === 'dashboard') {
                setCurrentPage('analytics');
                setAnalyticsContext(context);

                let newPath = `/analytics`;
                if (context.organizationId) newPath += `/org/${context.organizationId}`;
                if (context.instanceId) newPath += `/instance/${context.instanceId}`;
                if (context.respondentId) newPath += `/respondent/${context.respondentId}`;
                if (context.templateId) newPath += `/template/${context.templateId}`;

                window.history.replaceState({ page: 'analytics', ...context }, '', newPath);
            } else {
                setCurrentPage(page);
                setAnalyticsContext(context);
            }
        };

        parseUrlAndNavigate(true);

        const handlePopState = () => parseUrlAndNavigate(false);
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isAuthenticated]);

    useEffect(() => {
        const handleUnauthorized = () => {
            logout();
            addToast('error', 'פג תוקף החיבור', 'המושב פג, אנא התחבר מחדש');
        };

        window.addEventListener('app:unauthorized', handleUnauthorized);
        return () => window.removeEventListener('app:unauthorized', handleUnauthorized);
    }, [logout, addToast]);

    useEffect(() => {
        if (!isAuthenticated) return;

        const fetchData = async () => {
            try {
                const [orgsData, templatesData, instancesData] = await Promise.all([
                    api.getOrganizations(),
                    api.getTemplates(),
                    api.getSurveys(),
                ]);

                setOrganizations(Array.isArray(orgsData) ? orgsData : []);
                setTemplates(Array.isArray(templatesData) ? templatesData : []);
                setInstances(Array.isArray(instancesData) ? instancesData : []);
            } catch (error: any) {
                console.error('Error fetching data:', error);
                // 401 is now handled globally via event
                if (!error.message?.includes('401')) {
                    addToast('error', 'שגיאה בטעינת נתונים', 'אנא נסה לרענן את הדף');
                }
            } finally {
                setLoadingOrgs(false);
                setLoadingTemplates(false);
                setLoadingInstances(false);
                setInitialLoading(false);
            }
        };

        fetchData();
    }, [addToast, isAuthenticated]);

    const handleCreateOrganization = async (payload: CreateOrganizationPayload) => {
        try {
            const newOrg = await api.createOrganization(payload);
            setOrganizations((prev) => [...prev, newOrg]);
            addToast('success', 'הארגון נוצר בהצלחה');
        } catch (error) {
            console.error('Error creating organization:', error);
            addToast('error', 'שגיאה ביצירת ארגון');
            throw error;
        }
    };

    const handleUpdateOrganization = async (payload: UpdateOrganizationPayload) => {
        try {
            const updated = await api.updateOrganization(payload);
            setOrganizations((prev) => prev.map((o) => (o.id === payload.org_id ? { ...o, ...updated } : o)));
            addToast('success', 'הארגון עודכן בהצלחה');
        } catch (error) {
            console.error('Error updating organization:', error);
            addToast('error', 'שגיאה בעדכון ארגון');
            throw error;
        }
    };

    const handleDeleteOrganization = async (orgId: string) => {
        try {
            await api.deleteOrganization(orgId);
            setOrganizations((prev) => prev.filter((o) => o.id !== orgId));
            addToast('success', 'הארגון נמחק בהצלחה');
        } catch (error) {
            console.error('Error deleting organization:', error);
            addToast('error', 'שגיאה במחיקת ארגון');
            throw error;
        }
    };

    const handleUpdateTemplateName = async (payload: UpdateTemplatePayload) => {
        try {
            await api.updateTemplateName(payload);
            setTemplates((prev) =>
                prev.map((t) => (t.id === payload.template_id ? { ...t, name: payload.name } : t))
            );
            addToast('success', 'שם התבנית עודכן בהצלחה');
        } catch (error) {
            console.error('Error updating template name:', error);
            addToast('error', 'שגיאה בעדכון שם התבנית');
            throw error;
        }
    };

    const handleCreateInstance = async (payload: CreateSurveyPayload) => {
        try {
            const newInstance = await api.createSurvey(payload);
            setInstances((prev) => [...prev, newInstance]);
            addToast('success', 'הסקר נוצר בהצלחה');
        } catch (error) {
            console.error('Error creating instance:', error);
            addToast('error', 'שגיאה ביצירת סקר');
            throw error;
        }
    };

    const handleUpdateInstance = async (payload: UpdateSurveyPayload) => {
        try {
            const updated = await api.updateSurvey(payload);
            setInstances((prev) =>
                prev.map((i) => (i.id === payload.instance_id ? { ...i, ...updated } : i))
            );
            addToast('success', 'הסקר עודכן בהצלחה');
        } catch (error) {
            console.error('Error updating instance:', error);
            addToast('error', 'שגיאה בעדכון סקר');
            throw error;
        }
    };

    const handleDeleteInstance = async (instanceId: string) => {
        try {
            await api.deleteSurvey(instanceId);
            setInstances((prev) => prev.filter((i) => i.id !== instanceId));
            addToast('success', 'הסקר נמחק בהצלחה');
        } catch (error) {
            console.error('Error deleting instance:', error);
            addToast('error', 'שגיאה במחיקת סקר');
            throw error;
        }
    };

    const renderPage = () => {
        switch (currentPage) {
            case 'dashboard':
                return (
                    <DashboardPage
                        organizations={organizations}
                        templates={templates}
                        instances={instances}
                        onNavigate={(page) => handleNavigation(page)}
                    />
                );
            case 'organizations':
                return (
                    <OrganizationsPage
                        organizations={organizations}
                        instances={instances}
                        templates={templates}
                        loading={loadingOrgs}
                        onCreate={handleCreateOrganization}
                        onUpdate={handleUpdateOrganization}
                        onDelete={handleDeleteOrganization}
                        onShowToast={addToast}
                        onNavigateToAnalytics={(organizationId) => {
                            handleNavigation('analytics', { organizationId });
                        }}
                        onNavigateToInstanceAnalytics={(instanceId) => {
                            const instance = instances.find(i => i.id === instanceId);
                            handleNavigation('analytics', {
                                instanceId,
                                organizationId: instance?.organization_id
                            });
                        }}
                        onNavigateToRespondent={(respondentId, context) => {
                            handleNavigation('analytics', { respondentId, ...context });
                        }}
                    />
                );
            case 'templates':
                return (
                    <TemplatesPage
                        templates={templates}
                        instances={instances}
                        organizations={organizations}
                        loading={loadingTemplates}
                        onUpdateName={handleUpdateTemplateName}
                        onShowToast={addToast}
                        onNavigateToInstanceAnalytics={(instanceId) => {
                            const instance = instances.find(i => i.id === instanceId);
                            handleNavigation('analytics', {
                                instanceId,
                                organizationId: instance?.organization_id
                            });
                        }}
                        onNavigateToRespondent={(respondentId, context) => {
                            handleNavigation('analytics', { respondentId, ...context });
                        }}
                    />
                );
            case 'instances':
                return (
                    <InstancesPage
                        instances={instances}
                        organizations={organizations}
                        templates={templates}
                        loading={loadingInstances}
                        onCreate={handleCreateInstance}
                        onUpdate={handleUpdateInstance}
                        onDelete={handleDeleteInstance}
                        onShowToast={addToast}
                        onNavigateToAnalytics={(instanceId) => {
                            const instance = instances.find(i => i.id === instanceId);
                            handleNavigation('analytics', {
                                instanceId,
                                organizationId: instance?.organization_id
                            });
                        }}
                        onNavigateToRespondent={(respondentId, context) => {
                            handleNavigation('analytics', { respondentId, ...context });
                        }}
                    />
                );
            case 'analytics':
                return (
                    <AnalyticsDashboardPage
                        instanceId={analyticsContext.instanceId}
                        templateId={analyticsContext.templateId}
                        organizationId={analyticsContext.organizationId}
                        respondentId={analyticsContext.respondentId}
                        onShowToast={addToast}
                        onNavigate={(params) => {
                            if (!params || Object.keys(params).length === 0) {
                                handleNavigation('organizations');
                            } else {
                                handleNavigation('analytics', params);
                            }
                        }}
                    />
                );
            default:
                return null;
        }
    };

    if (!isAuthenticated) {
        return <LoginPage />;
    }

    if (initialLoading) {
        return (
            <div
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--neutral-50)',
                }}
            >
                <LoadingSpinner size="lg" text="טוען את המערכת..." />
            </div>
        );
    }

    return (
        <>
            <Layout
                currentPage={currentPage}
                pageTitle={pageTitles[currentPage]}
                onNavigate={(page) => handleNavigation(page)}
                onLogout={logout}
            >
                {renderPage()}
            </Layout>

            <div className="toast-container">
                {toasts.map((toast) => (
                    <Toast key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id)} />
                ))}
            </div>
        </>
    );
};

const App: React.FC = () => {
    return (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <AuthProvider>
                <AppContent />
            </AuthProvider>
        </GoogleOAuthProvider>
    );
};

export default App;
